import React, { useState } from "react";
import { Lightbulb, TrendingUp, Layers, Search, BarChart3 } from "lucide-react";
import DataPanel from "../DataPanel";
import EmptyState from "../EmptyState";
import WidgetExportBtn from "../../reports/WidgetExportBtn";
import { flattenKeywords, flattenSerpFeatures } from "../../reports/exportDataUtils";

interface DiscoveryTabContentProps {
  liveData?: any | null;
  activeSubNav?: string;
}

const DiscoveryTabContent: React.FC<DiscoveryTabContentProps> = ({ liveData, activeSubNav }) => {
  const [activeTab, setActiveTab] = useState("all");
  
  // ─── SUB-NAV: SERP ───
  if (activeSubNav === "serp-disc") {
    const serpFeatures: any[] = liveData?.serpFeatures ?? [];
    const keywordMetrics: any[] = liveData?.keywordMetrics ?? [];

    return (
      <div className="space-y-6">
        <DataPanel title="SERP Feature Keywords" icon={<Search className="w-4 h-4" />} actions={<WidgetExportBtn title="SERP Feature Keywords" data={flattenSerpFeatures(serpFeatures)} />}>
          {serpFeatures.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Position", "Volume", "Traffic", "SERP Features"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {serpFeatures.slice(0, 15).map((sf: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{sf.keyword}</td>
                      <td className="py-2.5 px-2 font-bold" style={{ color: "#E8E4FF" }}>#{sf.position ?? "—"}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{(sf.volume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#FF9A4A" }}>{(sf.traffic ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex flex-wrap gap-1">
                          {(sf.serpFeatures ?? []).map((f: string, j: number) => (
                            <span key={j} className="text-[10px] px-1.5 py-0.5 rounded"
                              style={{ background: "rgba(91,61,245,0.15)", color: "#8B7BF7" }}>
                              {f}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No SERP feature data yet" description="Run a SERP Analysis audit to see which SERP features you appear in." icon={<Search className="w-6 h-6" style={{ color: "#8B7BF7" }} />} />
          )}
        </DataPanel>

        <DataPanel title="Keyword Metrics" icon={<BarChart3 className="w-4 h-4" />} actions={<WidgetExportBtn title="Keyword Metrics" data={keywordMetrics.map(km => ({ Keyword: km.keyword, Volume: km.volume, CPC: km.cpc, Difficulty: km.difficulty, Competition: km.competition }))} />}>
          {keywordMetrics.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Volume", "CPC", "Difficulty", "Competition"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {keywordMetrics.slice(0, 15).map((km: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{km.keyword}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(km.volume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>${(km.cpc ?? 0).toFixed(2)}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                            <div className="h-full rounded-full" style={{ width: `${km.difficulty ?? 0}%`, background: (km.difficulty ?? 0) > 60 ? "#F87171" : (km.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                          </div>
                          <span className="text-xs" style={{ color: "#9AA0B3" }}>{km.difficulty ?? "—"}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>{km.competition ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState title="No keyword metrics yet" description="Run a SERP Analysis audit to see detailed keyword metrics." />
          )}
        </DataPanel>
      </div>
    );
  }

  // ─── DEFAULT SUB-NAV: Keyword Discovery ───
  const keywords: any[] = liveData?.keywords ?? [];
  const hasKeywords = keywords.length > 0;

  const bySource = (src: string) => keywords.filter((k: any) => k.source === src);
  const similar = bySource("similar");
  const related = bySource("related");
  const questions = keywords.filter((k: any) => k.source === "questions" || k.keyword?.includes("?"));
  const longtail = bySource("longtail");

  const filteredKeywords =
    activeTab === "all"
      ? keywords
      : activeTab === "similar"
        ? similar
        : activeTab === "related"
          ? related
          : activeTab === "questions"
            ? questions
            : activeTab === "longtail"
              ? longtail
              : keywords;

  return (
    <div className="space-y-6">
      <DataPanel title="Keyword Opportunities" icon={<Lightbulb className="w-4 h-4" />} actions={<WidgetExportBtn title="Keyword Opportunities" data={flattenKeywords(keywords)} />}>
        {hasKeywords ? (
          <>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                  { key: "all", label: `All (${keywords.length})` },
                  { key: "similar", label: `Similar (${similar.length})` },
                  { key: "related", label: `Related (${related.length})` },
                  { key: "questions", label: `Questions (${questions.length})` },
                  { key: "longtail", label: `Longtail (${longtail.length})` },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                    style={{
                      background: activeTab === tab.key ? "rgba(91,61,245,0.2)" : "rgba(255,255,255,0.05)",
                      border: `1px solid ${activeTab === tab.key ? "rgba(91,61,245,0.3)" : "rgba(139,123,247,0.15)"
                        }`,
                      color: activeTab === tab.key ? "#8B7BF7" : "#9AA0B3",
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: "1px solid rgba(139,123,247,0.1)" }}>
                    {["Keyword", "Volume", "Difficulty", "CPC", "Source"].map((h) => (
                      <th key={h} className="text-left py-2 px-2 text-xs font-medium" style={{ color: "#9AA0B3" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredKeywords.slice(0, 10).map((kw: any, i: number) => (
                    <tr key={i} style={{ borderBottom: "1px solid rgba(139,123,247,0.06)" }}>
                      <td className="py-2.5 px-2" style={{ color: "#C4BDE0" }}>{kw.keyword}</td>
                      <td className="py-2.5 px-2" style={{ color: "#E8E4FF" }}>{(kw.volume ?? 0).toLocaleString()}</td>
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-1.5 rounded-full" style={{ background: "rgba(139,123,247,0.15)" }}>
                            <div className="h-full rounded-full"
                              style={{ width: `${kw.difficulty ?? 0}%`, background: (kw.difficulty ?? 0) > 40 ? "#FF9A4A" : "#4ADE80" }} />
                          </div>
                          <span className="text-xs" style={{ color: "#9AA0B3" }}>{kw.difficulty ?? "—"}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-2" style={{ color: "#9AA0B3" }}>${(kw.cpc ?? 0).toFixed(2)}</td>
                      <td className="py-2.5 px-2">
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "rgba(91,61,245,0.15)", color: "#8B7BF7" }}>
                          {kw.source ?? "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 pt-3 border-t text-xs" style={{ borderColor: "rgba(139,123,247,0.1)", color: "#9AA0B3" }}>
              Showing {Math.min(filteredKeywords.length, 10)} of <span style={{ color: "#FF9A4A" }}>{keywords.length}</span> discovered keywords
            </div>
          </>
        ) : (
          <EmptyState title="No keyword opportunities yet" description="Run a Keyword Discovery audit to find new opportunities." />
        )}
      </DataPanel>

      <DataPanel title="Trending Topics" icon={<TrendingUp className="w-4 h-4" />} actions={<WidgetExportBtn title="Trending Topics" data={[]} />}>
        <EmptyState title="No trending data yet" description="Run a Keyword Discovery audit to see trending topics." />
      </DataPanel>

      <DataPanel title="Topic Clusters" icon={<Layers className="w-4 h-4" />} actions={<WidgetExportBtn title="Topic Clusters" data={[]} />}>
        <EmptyState title="No cluster data yet" description="Run a Keyword Discovery audit to see topic clusters." />
      </DataPanel>
    </div>
  );
};

export default DiscoveryTabContent;
